import React, { useMemo } from "react";

import browserLocale from "browser-locale";
import capitalize from "lodash-es/capitalize";
import { defineMessage, IntlShape } from "react-intl";

import { AVAILABLE_UI_LANGUAGES } from "@app/util/consts";

import styles from "./useLanguageOptions.module.scss";

const autoDetectLanguage = defineMessage({
  id: "settings.profile.locale.autoDetect",
  defaultMessage: "Auto-detect",
  description: "Auto-detect language",
});

const useLanguageOptions = (intl: IntlShape) =>
  useMemo(() => {
    const currentLocale = intl.locale;
    const systemLocale = browserLocale();

    // choose a locale to show 2nd display name for the language option
    // if current locale matches browser's locale, use REACT_APP_DEFAULT_LOCALE for that, but only
    // if it's not the current locale already, then show nothing.
    let additionalNameLocale: string | undefined =
      currentLocale.substring(0, 2) === systemLocale.substring(0, 2)
        ? currentLocale.substring(0, 2) === process.env.REACT_APP_DEFAULT_LOCALE!.substring(0, 2)
          ? undefined
          : process.env.REACT_APP_DEFAULT_LOCALE
        : systemLocale;

    let formatter: { of: (language: string) => string };
    try {
      if (additionalNameLocale) {
        // TS doesn't know about Intl.DisplayName
        // @ts-ignore
        formatter = new Intl.DisplayNames(additionalNameLocale, {
          languageDisplay: "standard",
          type: "language",
        });
      }
    } catch {
      additionalNameLocale = undefined; // don't show 2nd name
    }

    return [
      {
        label: intl?.formatMessage(autoDetectLanguage),
        value: "",
      },
      ...AVAILABLE_UI_LANGUAGES.map((language) => ({
        label: (
          <div>
            {capitalize(intl.formatDisplayName(language, { type: "language" }))}
            {additionalNameLocale && formatter && (
              <span className={styles.secondName}>
                {" "}
                &mdash; {capitalize(formatter.of(language))}
              </span>
            )}
          </div>
        ),
        value: language,
      })),
    ];
  }, [intl]);

export default useLanguageOptions;
